import { useMemo } from 'react'

import { buildWatchAddresses } from '~/components/txn-display/state-change/utils/buildWatchAddresses'
import { isActiveOrgTransaction } from '~/components/txn-display/state-change/utils/isActiveOrgTransaction'
import { useActiveOrgId } from '~/hooks/org'
import { useSmartAccounts } from '~/hooks/useSmartAccounts'
import { OrgRegularTransaction, PublicRegularTransaction } from '~/types'

/**
 * Addresses whose balance changes the State change panel shows for a
 * transaction. `addressesToWatch` stays undefined while the active org's
 * account list loads, so the list is complete the first time it's used.
 */
export const useWatchAddresses = (
  transaction?: OrgRegularTransaction | PublicRegularTransaction,
) => {
  const activeOrgId = useActiveOrgId()
  const { smartAccounts, isLoading: isLoadingSmartAccounts } = useSmartAccounts(
    {
      includeRootAccount: true,
      includeAutomations: true,
      showArchived: true,
    },
  )

  const isLoading =
    !!transaction &&
    isActiveOrgTransaction({ transaction, activeOrgId }) &&
    isLoadingSmartAccounts

  const addressesToWatch = useMemo(() => {
    if (!transaction || isLoading) return undefined

    return buildWatchAddresses({
      transaction,
      activeOrgId,
      orgAccountAddresses: smartAccounts.map((account) => account.address),
    })
  }, [transaction, isLoading, activeOrgId, smartAccounts])

  return { addressesToWatch, isLoading }
}
